/** @prettier */

import {Auth} from 'aws-amplify';

//API import
import {
  getCompositionsGateway,
  getCompositionsGatewayByGatewayID,
  getSettingSites,
  getCompositionsGroups,
} from '../API/API_Collection';
//interface import
import * as apiIF from '../Type/api';

import {limit} from '../define';

/**
 * 各ユニットの登録状況
 * ユニットが存在していればtrue、存在しないか権限がなければfalse。
 */
export interface unitExistence {
  /** Q-tonの登録状況 */
  hasQton: boolean;
  /** Hyozan（室外機）の登録状況 */
  hasHyozanOU: boolean;
  /** Hyozan（室内機）の登録状況 */
  hasHyozanIU: boolean;
  /** Hydrolution PROの登録状況 */
  hasHydrolutionPro: boolean;
  /** HCCVQの登録状況 */
  hasHCCVQ: boolean;
  /** 空調機（室内機）の登録状況 */
  hasAirconIU?: boolean;
}

/**
 * ユニットの登録状況を取得する。
 * @param siteID サイトID
 * @param includeAircon 空調機の情報を含めるかどうか
 * @returns ユニットの登録状況
 */
export async function checkUnitExistence(
  siteID: string,
  includeAircon?: boolean
) {
  try {
    let result: unitExistence = {
      hasQton: false,
      hasHyozanOU: false,
      hasHyozanIU: false,
      hasHydrolutionPro: false,
      hasHCCVQ: false,
    };

    const promises = [];
    //ログインしているユーザのアドレスを取得
    promises.push(Auth.currentAuthenticatedUser());
    //権限情報を取得
    promises.push(
      getSettingSites(siteID, {
        'max-result': limit.REGISTRATION_NUM_AUTHORITY_MAX,
      })
    );
    const response = await Promise.all(promises);
    /** ログインしているユーザの情報 */
    const userInfo = response[0];
    /** 権限情報 */
    const authorityInfo = response[1] as apiIF.res.getSettingSites;
    /** ログインしているユーザの権限情報 */
    const userAuthority = authorityInfo['users-authority'].find(info => {
      return info['e-mail'] === userInfo.attributes.email;
    });
    if (
      userAuthority === undefined ||
      userAuthority['site-authority'] === 'none'
    ) {
      if (includeAircon === true) {
        result.hasAirconIU = false;
      }
      return result;
    }

    /** ゲートウェイの登録情報 */
    const gwRegisterInfo = await getCompositionsGateway(siteID, {
      'fields': 'register-number',
      'max-result': limit.REGISTRATION_NUM_GW_MAX,
    });
    // レスポンスからユニットの登録数を確認し、登録状況を更新
    /** 空調機（室内機）の登録状況 */
    let hasAirconIU = false;
    /** Q-tonの登録状況 */
    let hasQton = false;
    /** Hyozan（室外機）の登録状況 */
    let hasHyozanOU = false;
    /** Hyozan（室内機）の登録状況 */
    let hasHyozanIU = false;
    /** Hydrolution PROの登録状況 */
    let hasHydrolutionPro = false;
    /** HCCVQの登録状況 */
    let hasHCCVQ = false;
    for (const gwInfo of gwRegisterInfo['gateway-informations']) {
      if (gwInfo['register-number'] === undefined) continue;
      if (
        Number(gwInfo['register-number']['airconditioner']['indoor-unit']) > 0
      ) {
        hasAirconIU = true;
      }
      if (Number(gwInfo['register-number']['qton']) > 0) {
        hasQton = true;
      }
      if (Number(gwInfo['register-number']['hyozan']['outdoor-unit']) > 0) {
        hasHyozanOU = true;
      }
      if (Number(gwInfo['register-number']['hyozan']['indoor-unit']) > 0) {
        hasHyozanIU = true;
      }
      if (Number(gwInfo['register-number']['chiller']) > 0) {
        hasHydrolutionPro = true;
      }
      if (Number(gwInfo['register-number']['hccvq']) > 0) {
        hasHCCVQ = true;
      }
    }

    //Admin権限の場合
    if (userAuthority['site-authority'] === 'admin') {
      if (includeAircon === true) {
        result.hasAirconIU = hasAirconIU;
      }
      result.hasQton = hasQton;
      result.hasHyozanOU = hasHyozanOU;
      result.hasHyozanIU = hasHyozanIU;
      result.hasHydrolutionPro = hasHydrolutionPro;
      result.hasHCCVQ = hasHCCVQ;
      return result;
    }
    //それ以外の権限の場合
    if (includeAircon === true) {
      result.hasAirconIU = false;
      const allGroupInfo = await getCompositionsGroups(siteID, {
        'fields': 'registrations-number',
        'max-result': limit.REGISTRATION_NUM_GROUP_MAX,
      });
      for (const authority of userAuthority['airconditioner-authority']) {
        if (authority.authority === 'none') continue;
        const groupInfo = allGroupInfo['group-informations'].find(info => {
          return info['group-id'] === authority['group-id'];
        });
        if (
          groupInfo === undefined ||
          groupInfo['registrations-number'] === undefined
        ) {
          continue;
        }
        if (Number(groupInfo['registrations-number']) > 0) {
          result.hasAirconIU = true;
        }
      }
    }
    result.hasQton = hasQton && userAuthority['qton-authority'] === 'viewer';
    //prettier-ignore
    result.hasHyozanOU = hasHyozanOU && userAuthority['hyozan-authority'] === 'viewer';
    //prettier-ignore
    result.hasHyozanIU = hasHyozanIU && userAuthority['hyozan-authority'] === 'viewer';
    //prettier-ignore
    result.hasHydrolutionPro = hasHydrolutionPro && userAuthority['chiller-authority'] === 'viewer';
    result.hasHCCVQ = hasHCCVQ && userAuthority['hccvq-authority'] === 'viewer';
    return result;
  } catch {
    return undefined;
  }
}

/**
 * 指定したゲートウェイのユニットの登録有無を取得する。（Admin権限時のみ使用するため、権限の確認はしない）
 * @param siteID サイトID
 * @param gwID ゲートウェイID
 * @returns ユニットの登録状況(true or false)
 */
export async function checkUnitExistenceByGatewayID(
  siteID: string,
  gwID: string
) {
  try {
    let result = {
      hasQton: false,
      hasHyozanOU: false,
      hasHyozanIU: false,
      hasHydrolutionPro: false,
      hasHCCVQ: false,
      hasAirconOU: false,
      hasAirconIU: false,
    };

    /** ゲートウェイの登録情報 */
    const gwInfo = await getCompositionsGatewayByGatewayID(siteID, gwID);
    // レスポンスからユニットの登録数を確認し、登録状況を更新
    /** 空調機（室外機）の登録状況 */
    let hasAirconOU = false;
    /** 空調機（室内機）の登録状況 */
    let hasAirconIU = false;
    /** Q-tonの登録状況 */
    let hasQton = false;
    /** Hyozan（室外機）の登録状況 */
    let hasHyozanOU = false;
    /** Hyozan（室内機）の登録状況 */
    let hasHyozanIU = false;
    /** Hydrolution PROの登録状況 */
    let hasHydrolutionPro = false;
    /** HCCVQの登録状況 */
    let hasHCCVQ = false;
    if (gwInfo['register-number'] === undefined) return result;
    if (
      Number(gwInfo['register-number']['airconditioner']['outdoor-unit']) > 0
    ) {
      hasAirconOU = true;
    }
    if (
      Number(gwInfo['register-number']['airconditioner']['indoor-unit']) > 0
    ) {
      hasAirconIU = true;
    }
    if (Number(gwInfo['register-number']['qton']) > 0) {
      hasQton = true;
    }
    if (Number(gwInfo['register-number']['hyozan']['outdoor-unit']) > 0) {
      hasHyozanOU = true;
    }
    if (Number(gwInfo['register-number']['hyozan']['indoor-unit']) > 0) {
      hasHyozanIU = true;
    }
    if (Number(gwInfo['register-number']['chiller']) > 0) {
      hasHydrolutionPro = true;
    }
    if (Number(gwInfo['register-number']['hccvq']) > 0) {
      hasHCCVQ = true;
    }

    return (
      hasAirconOU ||
      hasAirconIU ||
      hasQton ||
      hasHyozanOU ||
      hasHyozanIU ||
      hasHydrolutionPro ||
      hasHCCVQ
    );
  } catch {
    return undefined;
  }
}
